import { motion } from "motion/react";
import { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { buildObjectPositionClasses, type FocalMap } from "@/components/FocusedImage";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { isTestMode } from "@/lib/testMode";

/**
 * Hero focal points — landscape source (3:2). Y drifts as container AR widens
 * so the central magic (sun rays + cat silhouette + light arcs) stays in frame.
 * Shared by the poster <img> and the looping <video> below; both MUST use the
 * exact same string so cover-cropping stays pixel-synchronous.
 *
 * Mobile (base/sm) is tuned to keep the cat + light beams in the upper-middle
 * of the visible frame, leaving the lower portion for the cinematic title.
 */
const HERO_FOCAL: FocalMap = {
  base: [50, 38],
  sm: [50, 36],
  md: [50, 34],
  lg: [50, 40],
  xl: [50, 42],
  "2xl": [50, 38],
};
const HERO_OBJECT_POSITION = buildObjectPositionClasses(HERO_FOCAL);
const HERO_FOCAL_HINT = `${HERO_FOCAL.lg![0]}%_${HERO_FOCAL.lg![1]}%`;

// Note: live reduced-motion state komt uit `useReducedMotion()` binnen het
// component zelf — die hook reageert ook op wijzigingen tijdens runtime.
// Detect data-saver / very slow networks — skip video to save bandwidth & LCP
const shouldSkipVideo = (() => {
  if (typeof navigator === "undefined") return false;
  const conn = (navigator as Navigator & {
    connection?: { saveData?: boolean; effectiveType?: string };
  }).connection;
  if (!conn) return false;
  if (conn.saveData) return true;
  if (conn.effectiveType === "slow-2g" || conn.effectiveType === "2g") return true;
  return false;
})();

// Pick mobile vs desktop video source once on mount
const isMobileViewport =
  typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches;

// Mobile performance fallback: skip video entirely on small screens.
// Keeps the poster image visible (instant LCP), avoids decoding a looping
// MP4 on low-power devices, and saves significant bandwidth on cellular.
// Override via ?heroVideo=1 for testing the mobile video on a phone.
const forceMobileVideo =
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).get("heroVideo") === "1";
const skipVideoOnMobile = isMobileViewport && !forceMobileVideo;

const HomeHero = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [, setVideoLoaded] = useState(false);
  // Defer mounting the <video> until after first paint so the preloaded
  // poster <img> becomes the LCP element. This eliminates the ~3s resource
  // load delay Lighthouse attributed to the video.
  const [videoMounted, setVideoMounted] = useState(false);
  // Desktop-only flag (lg breakpoint = 1024px). Used to switch the hero
  // video into a single-shot, hold-on-final-frame playback mode and to gate
  // the desktop mist overlay. Mobile + tablet behavior is fully preserved.
  const [isDesktop, setIsDesktop] = useState<boolean>(() =>
    typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches
  );
  // Tracks whether the desktop video has reached its final frame. We use this
  // to fade out decorative desktop-only motion (mist drift, warm pulse) so the
  // final frame reads as an intentional, calm tableau.
  const [desktopVideoEnded, setDesktopVideoEnded] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(min-width: 1024px)");
    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  const prefersReducedMotion = useReducedMotion();
  // Test-modus = visuele regressie: behandel als reduced motion zodat snapshots
  // altijd op het posterframe vastliggen en geen entrance-animaties flakkeren.
  const testMode = isTestMode();
  const freezeMotion = prefersReducedMotion || testMode;

  // Reduced-motion users krijgen géén autoplay-video — alleen de stille poster.
  // Dit voorkomt parallax-/loop-beweging die misselijkheid kan veroorzaken.
  // Test-only override: ?heroForceVideo=1 dwingt de <video> render-pad af zodat
  // visual-regression specs poster vs video alignment kunnen vergelijken zonder
  // de runtime defaults aan te passen. Heeft geen effect zonder testMode-flag.
  const forceVideoForTest =
    testMode &&
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("heroForceVideo") === "1";
  const useStaticPoster =
    !forceVideoForTest && (shouldSkipVideo || freezeMotion || skipVideoOnMobile);

  // Lazy-start video playback after first frame renders
  useEffect(() => {
    if (useStaticPoster) return;
    const video = videoRef.current;
    if (!video) return;
    const onCanPlay = () => setVideoLoaded(true);
    video.addEventListener("canplay", onCanPlay, { once: true });
    return () => video.removeEventListener("canplay", onCanPlay);
  }, [useStaticPoster, videoMounted]);

  // Mount the <video> after first paint (idle callback) so the poster <img>
  // is the LCP candidate and the video fetch never blocks LCP.
  useEffect(() => {
    if (useStaticPoster) return;
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    const schedule = w.requestIdleCallback
      ? (cb: () => void) => w.requestIdleCallback!(cb, { timeout: 1500 })
      : (cb: () => void) => window.setTimeout(cb, 400);
    const id = schedule(() => setVideoMounted(true));
    return () => {
      if (typeof id === "number") clearTimeout(id);
    };
  }, [useStaticPoster]);

  const videoSrc = isMobileViewport ? "/videos/hero-bg-mobile.mp4" : "/videos/hero-bg.mp4";
  const posterSrc = isMobileViewport ? "/videos/hero-poster-mobile.jpg" : "/videos/hero-poster.jpg";

  return (
    <section data-hero className="relative isolate w-full max-w-none overflow-hidden h-[100svh] min-h-[520px] max-h-[900px] min-[769px]:h-[72svh] min-[769px]:min-h-[520px] min-[769px]:max-h-[720px] lg:h-[88svh] lg:min-h-[600px] lg:max-h-[940px]">
      <div className="absolute inset-0 z-0 h-full w-full overflow-hidden bg-background">
        {/* Video layer — poster provides instant visual, video loads async */}
        <motion.div
          className="absolute inset-0 h-full w-full will-change-transform"
          initial={freezeMotion ? false : { scale: 1.06, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 3.5, ease: [0.22, 0.68, 0.35, 1] }}
        >
          {/* Always render the poster <img> first — it's preloaded with
              fetchpriority=high, so it becomes the LCP element instantly.
              The <video> mounts after first paint and visually replaces it. */}
          <img
            src={posterSrc}
            alt=""
            aria-hidden="true"
            fetchPriority="high"
            data-focus={HERO_FOCAL_HINT}
            data-hero-media
            className={`pointer-events-none absolute inset-0 h-full w-full object-cover ${HERO_OBJECT_POSITION}`}
          />
          {!useStaticPoster && videoMounted && (
            <video
              ref={videoRef}
              autoPlay
              muted
              loop={!isDesktop}
              playsInline
              preload="metadata"
              poster={posterSrc}
              disablePictureInPicture
              aria-hidden="true"
              data-focus={HERO_FOCAL_HINT}
              data-hero-media
              onEnded={() => {
                // Desktop-only: stop on the final frame, no loop, no reset.
                if (!isDesktop) return;
                const v = videoRef.current;
                if (!v) return;
                try {
                  v.pause();
                } catch {
                  /* noop */
                }
                setDesktopVideoEnded(true);
              }}
              className={`pointer-events-none absolute inset-0 h-full w-full object-cover ${HERO_OBJECT_POSITION}`}
              // Desktop-only filmic grade: very subtle contrast/saturation lift
              // + tiny warm tint. Mobile/tablet keep the original raw video so
              // their look is bit-identical to the previous build. GPU-cheap
              // (single CSS filter), no layout impact.
              style={isDesktop ? { filter: "contrast(1.06) saturate(1.05) brightness(1.01)" } : undefined}
            >
              <source src={videoSrc} type="video/mp4" />
            </video>
          )}
        </motion.div>

        {/* Top scrim for navbar readability — very subtle on mobile to avoid a visible dark band */}
        <div className="absolute inset-x-0 top-0 z-[11] h-16 sm:h-32 pointer-events-none bg-gradient-to-b from-black/8 via-black/2 to-transparent sm:from-black/20 sm:via-black/6" />

        {/* Combined overlay — single animated layer instead of multiple */}
        <motion.div
          className="absolute inset-0 z-10 pointer-events-none"
          initial={freezeMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 2.5, delay: 0.4, ease: [0.22, 0.68, 0.35, 1] }}
        >
          {/* Mobile uses a stronger bottom-fade so the title reads cleanly */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-black/55 sm:from-black/45 sm:via-black/20 sm:to-background" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_72%_60%_at_50%_45%,rgba(0,0,0,0.06)_0%,rgba(0,0,0,0.18)_100%)] sm:bg-[radial-gradient(ellipse_70%_60%_at_50%_45%,rgba(0,0,0,0.18)_0%,rgba(0,0,0,0.3)_100%)]" />
          {/* Vignette — merged into same animated container */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_70%_at_50%_50%,transparent_45%,rgba(0,0,0,0.12)_100%)]" />
          {/* Smooth bridge to next section — fade to background at the very bottom */}
          <div className="absolute inset-x-0 bottom-0 h-24 sm:h-32 bg-gradient-to-b from-transparent to-background" />
        </motion.div>

        {/* Warm glow pulse — tablet only (sm→lg). On desktop (lg+) the new
            cinematic mist stack below replaces it for a softer, more layered
            feel. Skipped entirely under reduced-motion. */}
        {!freezeMotion && (
          <motion.div
            className="absolute inset-0 z-10 pointer-events-none hidden sm:block lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.6, 0.4, 0.6] }}
            transition={{
              opacity: { duration: 8, delay: 1.5, repeat: Infinity, ease: "easeInOut" },
            }}
          >
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_45%,hsl(32_35%_52%_/_0.08)_0%,transparent_70%)]" />
          </motion.div>
        )}

        {/* Desktop-only cinematic mist (lg+) — three soft, layered warm
            radial gradients that drift slowly on the GPU (transform + opacity
            only). Subtle by design: behind the text, never over it. Fades out
            gracefully once the hero video has held its final frame so the
            tableau reads as intentional and calm. */}
        {!freezeMotion && (
          <motion.div
            className="absolute inset-0 z-10 pointer-events-none hidden lg:block"
            initial={{ opacity: 0 }}
            animate={{ opacity: desktopVideoEnded ? 0.55 : 1 }}
            transition={{ duration: desktopVideoEnded ? 2.4 : 2.0, delay: desktopVideoEnded ? 0 : 0.6, ease: [0.22, 0.68, 0.35, 1] }}
          >
            {/* Foreground mist — warm ivory drift, slow horizontal */}
            <motion.div
              className="absolute -inset-x-[12%] inset-y-0 will-change-transform"
              animate={desktopVideoEnded ? { x: 0 } : { x: ["-3%", "3%", "-3%"] }}
              transition={{ duration: 28, repeat: desktopVideoEnded ? 0 : Infinity, ease: "easeInOut" }}
              style={{
                background:
                  "radial-gradient(ellipse 55% 38% at 38% 58%, hsl(var(--accent) / 0.10) 0%, transparent 65%)",
              }}
            />
            {/* Midground mist — golden ivory pool, vertical drift */}
            <motion.div
              className="absolute -inset-y-[10%] inset-x-0 will-change-transform"
              animate={desktopVideoEnded ? { y: 0 } : { y: ["1.5%", "-1.5%", "1.5%"] }}
              transition={{ duration: 36, repeat: desktopVideoEnded ? 0 : Infinity, ease: "easeInOut" }}
              style={{
                background:
                  "radial-gradient(ellipse 65% 45% at 62% 42%, hsl(var(--primary) / 0.07) 0%, transparent 70%)",
              }}
            />
            {/* Background depth — very wide, very faint warm wash */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  "radial-gradient(ellipse 90% 70% at 50% 35%, hsl(var(--accent) / 0.045) 0%, transparent 75%)",
              }}
            />
          </motion.div>
        )}

        {/* Desktop-only volumetric forest fog (lg+) — replaces the perception
            of a flat white mist band with multiple feathered, warm-ivory fog
            layers that drift horizontally and breathe vertically at different
            speeds. Pure transform/opacity animation, no layout impact.
            Skipped under reduced motion. */}
        {!freezeMotion && (
          <motion.div
            className="absolute inset-x-0 bottom-0 z-[11] h-[55%] pointer-events-none hidden lg:block overflow-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: desktopVideoEnded ? 0.85 : 1 }}
            transition={{ duration: 3.2, delay: 0.8, ease: [0.22, 0.68, 0.35, 1] }}
            aria-hidden
          >
            {/* Layer A — dense low foreground fog, slow horizontal drift */}
            <motion.div
              className="absolute -inset-x-[18%] bottom-[-8%] h-[70%] will-change-transform"
              animate={{ x: ["-4%", "4%", "-4%"] }}
              transition={{ duration: 42, repeat: Infinity, ease: "easeInOut" }}
              style={{
                background:
                  "radial-gradient(ellipse 60% 70% at 30% 90%, hsl(var(--background) / 0.55) 0%, hsl(var(--background) / 0.18) 40%, transparent 75%), radial-gradient(ellipse 55% 65% at 70% 95%, hsl(var(--background) / 0.45) 0%, hsl(var(--background) / 0.14) 45%, transparent 78%)",
                filter: "blur(2px)",
              }}
            />
            {/* Layer B — wider transparent fog passing behind logo area */}
            <motion.div
              className="absolute -inset-x-[14%] bottom-[6%] h-[60%] will-change-transform"
              animate={{ x: ["3%", "-3%", "3%"], y: ["1%", "-1.5%", "1%"] }}
              transition={{ duration: 56, repeat: Infinity, ease: "easeInOut" }}
              style={{
                background:
                  "radial-gradient(ellipse 70% 55% at 50% 70%, hsl(38 28% 90% / 0.32) 0%, hsl(38 28% 90% / 0.08) 50%, transparent 80%)",
                filter: "blur(3px)",
              }}
            />
            {/* Layer C — thin atmospheric haze around mid-trees */}
            <motion.div
              className="absolute inset-x-0 top-0 h-[55%] will-change-transform"
              animate={{ x: ["-2%", "2%", "-2%"] }}
              transition={{ duration: 70, repeat: Infinity, ease: "easeInOut" }}
              style={{
                background:
                  "radial-gradient(ellipse 80% 50% at 50% 50%, hsl(var(--accent) / 0.06) 0%, transparent 70%)",
                filter: "blur(2px)",
              }}
            />
            {/* Layer D — drifting wisps, very slow, subtle vertical breathing */}
            <motion.div
              className="absolute -inset-x-[10%] bottom-[20%] h-[35%] will-change-transform"
              animate={{ y: ["2%", "-2%", "2%"], opacity: [0.55, 0.75, 0.55] }}
              transition={{ duration: 48, repeat: Infinity, ease: "easeInOut" }}
              style={{
                background:
                  "radial-gradient(ellipse 35% 45% at 22% 60%, hsl(38 28% 92% / 0.22) 0%, transparent 70%), radial-gradient(ellipse 30% 40% at 78% 50%, hsl(38 28% 92% / 0.18) 0%, transparent 72%)",
                filter: "blur(4px)",
              }}
            />
          </motion.div>
        )}

        {/* Desktop-only firefly particles (lg+) — extremely sparse, warm
            ivory-gold, slow independent drift. Positioned in the darker
            forest zones, never above the navbar (top 18%+). Pure transform +
            opacity. Disabled under reduced motion. */}
        {!freezeMotion && (
          <div
            className="absolute inset-0 z-[12] pointer-events-none hidden lg:block overflow-hidden"
            aria-hidden
          >
            {[
              { left: "12%", top: "48%", size: 4, delay: 0,   dur: 11, drift: 18 },
              { left: "22%", top: "62%", size: 3, delay: 2.5, dur: 13, drift: 14 },
              { left: "78%", top: "44%", size: 4, delay: 1.2, dur: 12, drift: -16 },
              { left: "88%", top: "58%", size: 3, delay: 4,   dur: 14, drift: 12 },
              { left: "8%",  top: "72%", size: 3, delay: 3.5, dur: 15, drift: -12 },
              { left: "92%", top: "70%", size: 3, delay: 5.5, dur: 13, drift: 16 },
              { left: "32%", top: "38%", size: 2, delay: 6,   dur: 16, drift: 10 },
              { left: "68%", top: "36%", size: 2, delay: 1.8, dur: 14, drift: -10 },
            ].map((f, i) => (
              <motion.span
                key={i}
                className="absolute rounded-full will-change-transform"
                style={{
                  left: f.left,
                  top: f.top,
                  width: f.size,
                  height: f.size,
                  background:
                    "radial-gradient(circle, hsl(38 65% 78% / 0.85) 0%, hsl(32 35% 52% / 0.35) 50%, transparent 75%)",
                  boxShadow: "0 0 8px hsl(38 65% 75% / 0.45), 0 0 16px hsl(32 35% 52% / 0.18)",
                }}
                animate={{
                  y: [0, -f.drift * 0.6, f.drift * 0.4, 0],
                  x: [0, f.drift * 0.5, -f.drift * 0.3, 0],
                  opacity: [0, 0.7, 0.35, 0.65, 0],
                }}
                transition={{
                  duration: f.dur,
                  delay: f.delay,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
            ))}
          </div>
        )}

        {/* Desktop-only soft cinematic vignette (lg+) — adds depth separation
            between foreground mist and background trees without darkening the
            center. Static, zero animation cost. */}
        <div
          className="absolute inset-0 z-[11] pointer-events-none hidden lg:block"
          aria-hidden
          style={{
            background:
              "radial-gradient(ellipse 95% 80% at 50% 45%, transparent 55%, hsl(25 25% 8% / 0.18) 100%)",
          }}
        />
      </div>

      {/*
        Responsieve top-offset = navbar (60–68px) + safe-area + ademruimte.
        - <360px:  76px → eyebrow blijft volledig zichtbaar onder de compacte navbar
        - ≥360px:  88px → ruimere lucht boven de eyebrow op standaard mobiel
        - ≥431px: 104px → past bij grotere telefoons / hogere navbar-presence
        - ≥640px: 120px → tablet-formaat met meer cinematische headroom
        Bottom-padding schaalt mee zodat de CTA nooit tegen de viewport-rand of
        een mobiele browser-UI plakt.
      */}
      <div data-hero-card className="relative z-20 mx-auto flex h-full w-full max-w-3xl flex-col items-center justify-center px-4 pt-[calc(env(safe-area-inset-top,0px)+88px)] pb-10 text-center min-[431px]:px-5 min-[431px]:pt-[calc(env(safe-area-inset-top,0px)+104px)] min-[431px]:pb-12 sm:pt-[calc(env(safe-area-inset-top,0px)+120px)] sm:pb-16 min-[769px]:hidden">
        <div
          className="w-full rounded-[1.75rem] border border-white/20 bg-background/84 px-5 py-5 text-left shadow-[0_10px_40px_-18px_rgba(0,0,0,0.35)] backdrop-blur-xl min-[431px]:py-6"
        >
          {/* LCP-critical text — rendered eagerly without opacity:0 entrance so
              it paints on the first frame (instead of waiting for JS hydration
              + a 1.3s stagger delay). Preserves visual identity; only the CTA
              row keeps a subtle fade-in for cinematic warmth. */}
          <p
            className="text-[10.5px] font-body tracking-[0.2em] uppercase text-accent mb-2.5 min-[431px]:text-[11px] min-[431px]:tracking-[0.22em] min-[431px]:mb-3"
          >
            Praktijk Sprankelien · Ramsel
          </p>
          <h1
            className="font-heading italic text-foreground text-[2rem] leading-[1.12] tracking-tight max-w-[13ch] min-[431px]:text-[2.125rem] min-[431px]:leading-[1.08] min-[431px]:max-w-[12ch]"
          >
            Een warme plek om te sprankelen
          </h1>
          <p
            className="mt-3 max-w-[34ch] text-[15px] font-body leading-[1.6] text-foreground/90 min-[431px]:mt-4 min-[431px]:text-[16px] min-[431px]:leading-[1.6] min-[431px]:max-w-[30ch]"
          >
            Speltherapie, kinderyoga &amp; zomerkampen — met zachtheid en aandacht voor elk kind.
          </p>
          <motion.div
            initial={freezeMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, delay: 0.4, ease: [0.22, 0.68, 0.35, 1] }}
            className="mt-6 flex flex-col gap-3 min-[431px]:flex-row min-[431px]:items-center"
          >
            <Link
              to="/contact"
              className="btn-premium relative inline-flex min-h-[52px] items-center justify-center overflow-hidden rounded-full bg-primary px-7 text-[15px] font-semibold text-primary-foreground"
            >
              <span className="absolute inset-0 -translate-x-full animate-[shimmer_4.5s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-white/12 to-transparent" />
              <span className="relative">Neem contact op</span>
            </Link>
            <Link
              to="/over-mij"
              className="inline-flex min-h-[52px] items-center gap-2 self-start text-[15px] font-semibold text-foreground/78"
            >
              Ontdek wie we zijn
              <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HomeHero;
